import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useQueryClient } from "@tanstack/react-query";
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  Rating,
  TextField,
  Typography,
  FormHelperText,
} from "@mui/material";

import { GO_SERVICE_QUERY_KEYS } from "../../../../../api/queryKeys";
import { ServiceTypeEnum } from "../../../../../enum/service.enum";
import { useAddServiceReview } from "../../../../../api/mutations/useAddServiceReview";
import { reviewServiceSchema, ReviewServiceData } from "../../../../../schemas/review.schema";

const ReviewService = ({
  requestId,
  serviceId,
  userId,
  serviceType,
  isProvider,
}: {
  requestId: number;
  serviceId: number;
  userId: number;
  serviceType: ServiceTypeEnum;
  isProvider: boolean;
}) => {
  const { t } = useTranslation("goService", { keyPrefix: "serviceRequestPage" });
  const [isOpen, setIsOpen] = useState(false);
  const { mutate: addServiceReview } = useAddServiceReview();
  const {
    control,
    handleSubmit,
    reset,
    formState: { errors, isValid },
  } = useForm<ReviewServiceData>({
    resolver: zodResolver(reviewServiceSchema),
    defaultValues: {
      rating: 0,
      comment: "",
    },
  });

  const handleOpen = () => setIsOpen(true);
  const handleClose = () => {
    setIsOpen(false);
    reset();
  };

  const queryClient = useQueryClient();
  const onSubmit = (data: ReviewServiceData) => {
    addServiceReview(
      {
        requestId,
        serviceId,
        userId,
        data: { ...data, type: serviceType },
      },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({
            queryKey: [...GO_SERVICE_QUERY_KEYS.serviceFeedback(userId), { serviceId }],
          });
        },
      }
    );
    handleClose();
  };

  return (
    <>
      <Divider sx={styles.divider} />
      <Button variant="outlined-sm" sx={styles.reviewButton} onClick={handleOpen}>
        {t("addReview")}
      </Button>
      <Dialog open={isOpen} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle sx={styles.dialogTitle}>{t("rateProviderService")}</DialogTitle>
        <DialogContent>
          <Box component="form" onSubmit={handleSubmit(onSubmit)} sx={styles.form}>
            <Box sx={styles.ratingContainer}>
              <Typography sx={styles.ratingLabel}>
                {isProvider ? t("howWouldYouRateThisRequester") : t("howWouldYouRateThisService")}
              </Typography>
              <Controller
                name="rating"
                control={control}
                render={({ field: { onChange, value } }) => (
                  <>
                    <Rating
                      value={value}
                      onChange={(_, newValue) => onChange(newValue)}
                      size="large"
                      sx={styles.rating}
                    />
                    {errors.rating && (
                      <FormHelperText error sx={styles.errorText}>
                        {t(errors.rating.message)}
                      </FormHelperText>
                    )}
                  </>
                )}
              />
            </Box>
            <Box sx={styles.feedbackContainer}>
              <Typography sx={styles.feedbackLabel}>{t("writeYourFeedback")}</Typography>
              <Controller
                name="comment"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    multiline
                    rows={4}
                    placeholder={t("yourFeedback")}
                    fullWidth
                    sx={styles.feedbackField}
                    error={!!errors.comment}
                    helperText={errors.comment && t(errors.comment.message)}
                  />
                )}
              />
            </Box>
          </Box>
        </DialogContent>
        <Divider sx={styles.divider} />
        <DialogActions sx={styles.dialogActions}>
          <Button variant="outlined-md" onClick={handleClose}>
            {t("cancel")}
          </Button>
          <Button variant="primary-md" onClick={handleSubmit(onSubmit)} disabled={!isValid}>
            {t("submitFeedback")}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ReviewService;

const styles = {
  form: {
    display: "flex",
    flexDirection: "column",
    gap: "35px",
  },
  divider: {},
  reviewButton: {
    width: "fit-content",
  },
  dialogTitle: {
    color: "var(--Neutral-neutral-900, #0B0920)",
    fontFamily: "Montserrat",
    fontSize: "18px",
    fontStyle: "normal",
    fontWeight: 600,
    lineHeight: "28px",
    pb: "48px",
  },
  ratingContainer: {
    display: "flex",
    flexDirection: "column",
    gap: "28px",
  },
  ratingLabel: {
    fontFamily: "Montserrat",
    color: "var(--Dark---1000, #111)",
    fontSize: "18px",
    fontStyle: "normal",
    fontWeight: 600,
    lineHeight: "28px",
  },
  rating: {
    "& .MuiRating-icon": {
      fontSize: "32px",
    },
  },
  feedbackContainer: {
    display: "flex",
    flexDirection: "column",
    gap: "8px",
  },
  feedbackLabel: {
    fontFamily: "Montserrat",
    color: "var(--Dark---1000, #111)",
    fontSize: "18px",
    fontStyle: "normal",
    fontWeight: 600,
    lineHeight: "28px",
  },
  feedbackField: {
    "& .MuiInputBase-root.MuiOutlinedInput-root": {
      borderRadius: "8px",
      border: "none",
      background: "var(--Gray---50, #F5F6F5)",
    },
    "& .MuiOutlinedInput-notchedOutline": {
      border: "none",
    },
  },
  dialogActions: {
    padding: "16px 24px",
    gap: "12px",
  },
  errorText: {
    textAlign: "center",
    mt: 1,
  },
};
